import { Button } from '@codegouvfr/react-dsfr/Button';
import { useTextes } from '@common/config';
import { useSessionContext } from "@common/hooks";
import { useGetEtablissementPrincipal } from '@common/hooks/etablissement';
import { useOfaDetails } from '@common/hooks/organisme';
import {
  EtablissementInfoDto,
  EtablissementProjection,
  TypeDeclaration,
  useGetOrganismeEtablissementQuery,
} from '@common/services';
import { getModalById } from '@common/ui';
import { EditEtablissementInfoForm } from '@common/ui/etablissement';
import { EtablissementEditInfoModaldHandles } from '@common/ui/src/etablissement/form/EditEtablissementInfoForm';
import {
  TypeDeclarationChangeConfirmationModal,
} from '@common/ui/src/etablissement/modals/TypeDeclarationChangeConfirmationModal';
import { DateUtils } from '@common/utils';
import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// bien laisser cette ligne en dehors du composant pour la modale de confirmation s'affiche
const typeDeclarationChangeValidationModal = getModalById('type-declaration-change-validation-modal');

export const EditOfaEtablissement = () => {
  const t = useTextes('ParcoursOfa', 'EtapierStep2');
  const navigate = useNavigate();
  const { etablissementId } = useParams<EditOfaEtablissementParams>();
  const { organismeId } = useSessionContext();
  const { organisme } = useOfaDetails({ id: organismeId });

  const buttonSubmitRef = useRef<HTMLButtonElement | null>(null);

  const mode: Mode = (etablissementId) ? 'edition' : 'ajout';

  const { etablissementPrincipal, isLoading: isEtablissementPrincipalLoading } = useGetEtablissementPrincipal();
  const [typeDeclarationTarget, setTypeDeclarationTarget] = useState<TypeDeclaration | null>(null);
  const editEtablissementInfoFormRef = useRef<EtablissementEditInfoModaldHandles | null>(null);

  const {
    data: etablissementToEdit = {} as EtablissementInfoDto,
    isLoading: isEtablissementToEditLoading,
    error: etablissementToEditError,
  } = useGetOrganismeEtablissementQuery({
    id: organismeId,
    etablissementId,
    projection: EtablissementProjection.INFO,
  }, { skip: !organismeId || !etablissementId });

  if (isEtablissementPrincipalLoading || isEtablissementToEditLoading) return null;

  if ((mode === 'edition') && (etablissementToEditError?.status === 404)) {
    return <div>L'établissement n'existe pas ou n'appartient pas à cet organisme.</div>;
  }

  const title = (mode === 'ajout') ? 'Ajouter un établissement'
    : (etablissementToEdit.id) ?
      <span>Modification de l'établissement{(etablissementToEdit.principal) ? ' principal' : ''} {etablissementToEdit.denominationUsuelle}</span>
      : `CFA ${etablissementToEdit.denominationUsuelle}`;

  const typeDeclarationChangeConfirmationHandle = (typeDeclaration: TypeDeclaration) => {
    setTypeDeclarationTarget(typeDeclaration);
    typeDeclarationChangeValidationModal.open();
  };

  const onConfirmTypeDeclarationChangeModal = async () => {
    typeDeclarationChangeValidationModal.close();
    await editEtablissementInfoFormRef.current?.onTypeDeclarationChangeConfirmation();
  };

  const onCancelTypeDeclarationChangeModal = async () => {
    // on ne fait rien si la modal de confirmation a été cancelml
  };

  return (
    <div className={'flex flex-col gap-4 items-center p-4 pb-8'}>

      <div className={'flex flex-col w-full max-w-[1200px] gap-6'}>
        <div className={'flex items-center'}>
          <h3 className={'flex-1 !mb-1'}>{title}</h3>
        </div>
        <span className={'mb-4'}>{t['ETAB_OFA_PRIN_TEXTE2']}</span>
      </div>

      <div className={'flex flex-col w-full bg-white max-w-[1200px] py-[56px] px-[102px]'}>

        <div className={'flex flex-col gap-6'}>

          <h6 style={{ margin: 0 }} className={'pb-4 border-b-1 border-[#DDDDDD]'}>Identité de
            l’établissement {(etablissementToEdit.principal || !etablissementPrincipal) ? 'principal' : ''} déclarant
          </h6>

          {(mode === 'edition') && (
            <span className={'text-sm text-[#484D7A]'}>
              Ajouté le {DateUtils.formatDdMmYyyy(etablissementToEdit.dateCreation)}
            </span>
          )}

          <span className={'text-sm text-[#484D7A]'}>Les champs marqués d’un * sont obligatoires</span>

          <EditEtablissementInfoForm
            ref={editEtablissementInfoFormRef}
            etablissement={(mode === 'ajout') ? undefined : etablissementToEdit}
            etablissementPrincipal={etablissementPrincipal}
            isAddMode={(mode === 'ajout')}
            organisme={organisme}
            buttonSubmitRef={buttonSubmitRef}
            submitHandler={() => navigate(`/mon-ofa/edition/etablissement`)}
            typeDeclarationChangeHandler={typeDeclarationChangeConfirmationHandle} />

          <TypeDeclarationChangeConfirmationModal dsfrModal={typeDeclarationChangeValidationModal}
                                                  typeDeclarationTarget={typeDeclarationTarget}
                                                  onConfirm={onConfirmTypeDeclarationChangeModal}
                                                  onCancel={onCancelTypeDeclarationChangeModal} />

          <div className={'flex mt-8 justify-end gap-2'}>
            <Button style={{ color: '#E1000F', borderColor: '#E1000F', boxShadow: 'none' }}
                    priority={'secondary'}
                    onClick={() => navigate(-1)}>
              ANNULER
            </Button>
            <Button onClick={() => buttonSubmitRef.current?.click()}>
              {(mode === 'ajout') ? 'AJOUTER L\'ÉTABLISSEMENT' : 'MODIFIER L\'ÉTABLISSEMENT'} </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface EditOfaEtablissementParams {
  etablissementId: number | undefined;
}

type Mode = 'ajout' | 'edition'